import type { ComponentType } from "react";
import {
  ChartBarIcon,
  ChatAlt2Icon,
  ChipIcon,
  CollectionIcon,
  ColorSwatchIcon,
  CreditCardIcon,
  CubeIcon,
  DeviceMobileIcon,
  FolderIcon,
  OfficeBuildingIcon,
  PhotographIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/outline";

export type SidebarRole = "creator" | "admin" | "enduser";

export type SidebarSectionId =
  | "favorites"
  | "conception"
  | "deployment"
  | "analysis"
  | "resources"
  | "parameters"
  | "support";

export type SidebarItemId =
  | "create"
  | "courses"
  | "studio"
  | "learners"
  | "installations"
  | "statistics"
  | "myStatistics"
  | "medias"
  | "templates"
  | "themes"
  | "agents"
  | "profile"
  | "organization"
  | "team"
  | "subscription"
  | "helpCenter"
  | "wixarpedia";

export interface SidebarItemDef {
  id: SidebarItemId;
  section: Exclude<SidebarSectionId, "favorites">;
  titleKey: string;
  icon: ComponentType<{ className?: string }>;
  routePath?: string;
  action?: "studio" | "create";
  external?: boolean;
  dataTour?: string;
  roles: SidebarRole[];
  favoritable: boolean;
}

export interface SidebarSectionDef {
  id: SidebarSectionId;
  titleKey: string;
  bottom: boolean;
}

export const SIDEBAR_SECTIONS: SidebarSectionDef[] = [
  { id: "favorites", titleKey: "sidebar.sections.favorites", bottom: false },
  { id: "conception", titleKey: "sidebar.sections.conception", bottom: false },
  { id: "deployment", titleKey: "sidebar.sections.deployment", bottom: false },
  { id: "analysis", titleKey: "sidebar.sections.analysis", bottom: false },
  { id: "resources", titleKey: "sidebar.sections.resources", bottom: true },
  { id: "parameters", titleKey: "sidebar.sections.parameters", bottom: true },
  { id: "support", titleKey: "sidebar.sections.support", bottom: true },
];

const CREATORS: SidebarRole[] = ["creator", "admin"];
const EVERYONE: SidebarRole[] = ["creator", "admin", "enduser"];

export const SIDEBAR_ITEMS: SidebarItemDef[] = [
  {
    id: "create",
    section: "conception",
    titleKey: "sidebar.items.create",
    icon: PlusIcon,
    action: "create",
    roles: CREATORS,
    favoritable: false,
  },
  {
    id: "courses",
    section: "conception",
    titleKey: "sidebar.items.courses",
    icon: FolderIcon,
    routePath: "/courses",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "studio",
    section: "conception",
    titleKey: "sidebar.items.studio",
    icon: CubeIcon,
    action: "studio",
    external: true,
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "learners",
    section: "deployment",
    titleKey: "sidebar.items.learners",
    icon: UsersIcon,
    routePath: "/learners",
    dataTour: "sidebar-learners",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "installations",
    section: "deployment",
    titleKey: "sidebar.items.installations",
    icon: DeviceMobileIcon,
    routePath: "/installations",
    dataTour: "sidebar-installations",
    roles: EVERYONE,
    favoritable: true,
  },
  {
    id: "statistics",
    section: "analysis",
    titleKey: "sidebar.items.statistics",
    icon: ChartBarIcon,
    routePath: "/statistics",
    dataTour: "sidebar-statistics",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "myStatistics",
    section: "analysis",
    titleKey: "sidebar.items.myStatistics",
    icon: ChartBarIcon,
    routePath: "/stats",
    roles: ["enduser"],
    favoritable: true,
  },
  {
    id: "medias",
    section: "resources",
    titleKey: "sidebar.items.medias",
    icon: PhotographIcon,
    routePath: "/medias",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "templates",
    section: "resources",
    titleKey: "sidebar.items.templates",
    icon: CollectionIcon,
    routePath: "/templates",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "themes",
    section: "resources",
    titleKey: "sidebar.items.themes",
    icon: ColorSwatchIcon,
    routePath: "/themes",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "agents",
    section: "resources",
    titleKey: "sidebar.items.agents",
    icon: ChipIcon,
    routePath: "/agents",
    roles: CREATORS,
    favoritable: true,
  },
  {
    id: "profile",
    section: "parameters",
    titleKey: "sidebar.items.profile",
    icon: UserIcon,
    routePath: "/profile/me",
    dataTour: "sidebar-profile",
    roles: EVERYONE,
    favoritable: true,
  },
  {
    id: "organization",
    section: "parameters",
    titleKey: "sidebar.items.organization",
    icon: OfficeBuildingIcon,
    routePath: "/profile/organization",
    roles: EVERYONE,
    favoritable: true,
  },
  {
    id: "team",
    section: "parameters",
    titleKey: "sidebar.items.team",
    icon: UserGroupIcon,
    routePath: "/team",
    dataTour: "sidebar-team",
    roles: ["admin"],
    favoritable: true,
  },
  {
    id: "subscription",
    section: "parameters",
    titleKey: "sidebar.items.subscription",
    icon: CreditCardIcon,
    routePath: "/profile/subscription",
    roles: ["admin"],
    favoritable: true,
  },
  {
    id: "helpCenter",
    section: "support",
    titleKey: "sidebar.items.helpCenter",
    icon: QuestionMarkCircleIcon,
    routePath: "/help-center",
    roles: EVERYONE,
    favoritable: true,
  },
  {
    id: "wixarpedia",
    section: "support",
    titleKey: "sidebar.items.wixarpedia",
    icon: ChatAlt2Icon,
    routePath: "/chat/wixarpedia",
    roles: CREATORS,
    favoritable: true,
  },
];

export const getSidebarItemsForRole = (role: SidebarRole): SidebarItemDef[] =>
  SIDEBAR_ITEMS.filter((item) => item.roles.includes(role));
